/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';

import { AlertApiService } from './customer-api/alert-api.service';
import { NewsletterApiService } from './customer-api/newsletter-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { simpleHash } from 'src/app/shared/utils/simple-hash';

declare let pendo: any;

interface PendoInitParams {
  visitor: {
    id: string;
    SCORE_ALERT_QTY: number;
    EVENT_ALERT_QTY: number;
    NEWSLETTER_QTY: number;
    SCORE_TYPE: string;
    CAMPAIGN_ID: string;
    INTEREST: string;
    role?: string;
  };
  account: { id: string };
}

/**
 * Pendo analytics SDK initializer. Gates on user.email + tracking opt-outs,
 * then fires a forkJoin to compute alert/newsletter counts and calls
 * `pendo.initialize` with the resulting visitor + account payload.
 *
 * Fire-and-forget by design: the caller does NOT wait for the inner forkJoin
 * to resolve. Switching to a chained mergeMap would block login on the alert
 * + newsletter APIs.
 */
@Injectable({
  providedIn: 'root',
})
export class PendoService {
  constructor(
    private alertApi: AlertApiService,
    private newsletterApi: NewsletterApiService,
    private config: ConfigService,
  ) {}

  initIfEligible(user: any, accountInfo: any, defaultScore: string): void {
    let impersonate: string | null = null;
    if (this.config.appConfig.setup.useStorage) {
      impersonate = localStorage.getItem('impersonate');
    }
    if (!this.isEligible(user, impersonate)) return;

    forkJoin({
      scoreAlerts: this.alertApi.getAlerts('score'),
      eventAlerts: this.alertApi.getAlerts('event'),
      newsletters: this.newsletterApi.allNewsletter(),
    }).subscribe((res: any) => {
      const params = this.buildInitParams(user, accountInfo, defaultScore, res);
      pendo.initialize(params);
    });
  }

  private isEligible(user: any, impersonate: string | null): boolean {
    if (!user || !user.email) return false;
    if (impersonate === user.email) return false;
    if (this.config.appConfig.setup.noTracking) return false;
    if (
      Object.prototype.hasOwnProperty.call(user, 'roles') &&
      user.roles.includes('NO_TRACKING')
    )
      return false;
    if (
      Object.prototype.hasOwnProperty.call(user, 'groups') &&
      user.groups.includes('NO_TRACKING')
    )
      return false;
    return true;
  }

  private extractAccountId(user: any): string {
    let accountId = 'VISITOR-ACCOUNT-undefined';
    if (
      Object.prototype.hasOwnProperty.call(user, 'roles') &&
      user.roles.some((role: string) => role.includes('ORG'))
    ) {
      accountId = user.roles
        .find((role: string) => role.includes('ORG'))
        .split('_')[1];
    }
    if (
      Object.prototype.hasOwnProperty.call(user, 'groups') &&
      user.groups.some((group: string) => group.includes('ORG'))
    ) {
      accountId = user.groups
        .find((group: string) => group.includes('ORG'))
        .split('_')[1];
    }
    return accountId;
  }

  private extractRole(user: any): 'ADMIN' | null {
    if (
      Object.prototype.hasOwnProperty.call(user, 'roles') &&
      user.roles.includes('ADMIN')
    )
      return 'ADMIN';
    if (
      Object.prototype.hasOwnProperty.call(user, 'groups') &&
      user.groups.includes('ADMIN')
    )
      return 'ADMIN';
    return null;
  }

  private buildInitParams(
    user: any,
    accountInfo: any,
    defaultScore: string,
    res: any,
  ): PendoInitParams {
    const noScoreAlerts = res['scoreAlerts'].hits.filter(
      (e: any) => e.active,
    ).length;
    const noEventAlerts = res['eventAlerts'].hits.filter(
      (e: any) => e.active,
    ).length;
    const noNewsletters = res['newsletters'].filter(
      (e: any) => e.active,
    ).length;
    const userInterest = accountInfo?.interest
      ? `focus_${accountInfo.interest}`
      : null;

    const params: PendoInitParams = {
      visitor: {
        id: simpleHash(user.email),
        SCORE_ALERT_QTY: noScoreAlerts,
        EVENT_ALERT_QTY: noEventAlerts,
        NEWSLETTER_QTY: noNewsletters,
        SCORE_TYPE: defaultScore,
        CAMPAIGN_ID: accountInfo?.campaign_id,
        INTEREST: userInterest,
      },
      account: { id: this.extractAccountId(user) },
    };

    const role = this.extractRole(user);
    if (role) {
      params.visitor.role = role;
    }
    return params;
  }
}
